/**
 * AI CHAT STORE
 *
 * Zustand store for managing AI chat sessions (Claude Code, Codex).
 * All provider output is normalized to AiEvent schema. The handleEvent
 * reducer replays events into derived messages, tool activity, and file changes.
 *
 * All Tauri API calls use dynamic imports to avoid breaking web builds.
 */

import { create } from "zustand";
import { useChatTabStore } from "./chatTabStore";
import { isTauri } from "@/hooks/usePlatform";
import { loadSecretEnvForCwd } from "@/lib/secrets/runtime-env";
import type {
  AiProvider,
  AiEvent,
  AiChatSession,
  SessionState,
  SessionInfo,
  SessionGuardrails,
  DerivedMessage,
  DerivedTool,
  ReviewArtifact,
  ReviewResult,
  ReviewMode,
  ProviderStatus,
  TextDeltaPayload,
  ToolStartPayload,
  ToolOutputPayload,
  FileChangePayload,
  CostPayload,
  ErrorPayload,
  SessionStartPayload,
  SessionEndPayload,
} from "@/types/ai-chat";
import { DEFAULT_GUARDRAILS } from "@/types/ai-chat";

// ── Dynamic Tauri invoke helper ─────────────────────────────────────

async function tauriInvoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<T>(cmd, args);
}

function assertTauri(action: string): void {
  if (!isTauri()) {
    throw new Error(`${action} is only available in the desktop app`);
  }
}

// ── Review prompt template ──────────────────────────────────────────

function buildReviewPrompt(artifact: ReviewArtifact): string {
  const fileList = artifact.file_changes
    .map((f) => `  - ${f.path} (${f.change_type})`)
    .join("\n");

  return [
    "You are a senior code reviewer. Review the following changes and provide structured feedback.",
    "",
    `## Original Task`,
    artifact.original_prompt,
    "",
    `## Provider Summary`,
    artifact.provider_summary,
    "",
    `## Files Changed`,
    fileList || "  (none)",
    "",
    `## Git Diff`,
    "```diff",
    artifact.git_diff || "(no diff available)",
    "```",
    "",
    `## Cost`,
    `$${artifact.total_cost_usd.toFixed(4)} over ${artifact.event_count} events`,
    "",
    "Please provide:",
    "1. A verdict: approved or request_changes",
    "2. A brief summary of your review",
    "3. Specific findings (severity: critical/warning/suggestion, file, description)",
    "4. Suggested actions if changes are requested",
  ].join("\n");
}

// ── Bridge AI events to reviewStore activity boxes ──────────────────

// reviewStore and aiChatStore form an init-time circular dependency (reviewStore
// subscribes to this store at module top level), so reviewStore MUST be loaded
// lazily — a static import would run reviewStore's top-level subscribe before this
// module finishes initializing and throw. Dynamic import (cached) defers loading
// past init, replacing the previous CommonJS `require()` which is undefined in the
// Vite ESM browser bundle and silently threw on every non-text event.
let _reviewStorePromise: Promise<typeof import("./reviewStore")> | null = null;

function bridgeAiEventToActivity(event: AiEvent) {
  let content: string | undefined;
  let activityType: import("./reviewStore").ActivityType = "info";
  let toolName: string | undefined;
  let costUsd: number | undefined;

  switch (event.kind) {
    case "session_start":
      content = "AI session started";
      activityType = "info";
      break;
    case "tool_start": {
      const p = event.payload as ToolStartPayload;
      toolName = p.tool_name;
      content = `Using tool: ${p.tool_name}`;
      activityType = "tool";
      break;
    }
    case "tool_output": {
      const p = event.payload as ToolOutputPayload;
      toolName = p.tool_id;
      if (p.status === "error") {
        content = `Tool error: ${p.output_summary ?? "unknown"}`;
        activityType = "error";
      } else {
        content = `Tool completed: ${p.output_summary ?? p.tool_id}`;
        activityType = "success";
      }
      break;
    }
    case "cost": {
      const p = event.payload as CostPayload;
      costUsd = p.cost_usd;
      const tokens = p.input_tokens + p.output_tokens;
      content = `Cost: $${p.cost_usd.toFixed(4)} (${tokens.toLocaleString()} tokens)`;
      activityType = "cost";
      break;
    }
    case "error": {
      const p = event.payload as ErrorPayload;
      content = `Error: ${p.message}`;
      activityType = "error";
      break;
    }
    case "session_end":
      content = "AI session completed";
      activityType = "success";
      break;
    case "status": {
      const p = event.payload as unknown as Record<string, unknown>;
      const msg = typeof p.message === "string" ? p.message : "Thinking...";
      content = msg;
      activityType = "progress";
      break;
    }
    default:
      return;
  }

  if (!content) return;
  const activity = { content, activityType, toolName, costUsd };

  if (!_reviewStorePromise) _reviewStorePromise = import("./reviewStore");
  _reviewStorePromise
    .then(({ useReviewStore }) => {
      const reviewStore = useReviewStore.getState();
      const run = reviewStore.runs.find(
        (r) => r.activeSessionId === event.session_id || r.sessionIds?.includes(event.session_id),
      );
      if (!run) return; // no workflow run owns this session (standalone chat) — nothing to bridge
      reviewStore.ensureRunThread(run.id);
      reviewStore.appendActivity(run.id, {
        id: `ai-act-${event.seq}-${Date.now()}`,
        role: "system",
        content: activity.content,
        timestamp: event.ts,
        displayType: "activity",
        activityMeta: {
          activityType: activity.activityType,
          toolName: activity.toolName,
          costUsd: activity.costUsd,
        },
      });
    })
    .catch((err) => console.error("[aiChatStore] activity bridge failed:", err));
}

// ── Store Interface ─────────────────────────────────────────────────

interface AiChatStoreState {
  // State
  sessions: Map<string, AiChatSession>;
  activeSessionId: string | null;
  providerStatus: ProviderStatus[];
  reviewMode: ReviewMode;
  autoReview: boolean;
  maxReviewIterations: number;
  _pendingEvents: Map<string, AiEvent[]>;
  _sessionAliases: Map<string, string>;
  _drainPendingEvents: (sessionId: string) => void;

  // Getters
  activeSession: () => AiChatSession | null;

  // Provider detection
  checkProviders: () => Promise<void>;

  // Session actions
  createSession: (
    provider: AiProvider,
    cwd: string,
    prompt: string,
    resumeSessionId?: string,
  ) => Promise<string>;
  sendMessage: (sessionId: string, message: string) => Promise<void>;
  stopSession: (sessionId: string) => Promise<void>;
  setActiveSession: (sessionId: string | null) => void;

  // Hydration (restore persisted session from disk)
  hydrateSession: (sessionId: string) => Promise<boolean>;

  // Event handler (called by useAiChatEvents)
  handleEvent: (event: AiEvent) => void;

  // Review
  generateReviewArtifact: (sessionId: string) => Promise<ReviewArtifact | null>;
  startReview: (sessionId: string, reviewerProvider: AiProvider) => Promise<void>;
  applyFeedback: (sessionId: string, result: ReviewResult) => Promise<void>;
  setReviewMode: (mode: ReviewMode) => void;
  setAutoReview: (enabled: boolean) => void;

  // Settings
  setMaxReviewIterations: (n: number) => void;
}

// ── Helper: create empty session ────────────────────────────────────

function createEmptySession(
  id: string,
  provider: AiProvider,
  cwd: string,
  title: string,
): AiChatSession {
  return {
    id,
    provider,
    state: "idle",
    cwd,
    title,
    events: [],
    messages: [],
    toolActivity: [],
    fileChanges: [],
    totalCostUsd: 0,
    totalInputTokens: 0,
    totalOutputTokens: 0,
    lastSeq: -1,
  };
}

/**
 * Persist the full assembled conversation to disk so it survives an app restart.
 * The per-CLI-run event log only holds one process's assistant events, so it can't
 * restore user messages or a multi-turn (--resume) conversation; this snapshot is
 * the in-memory session exactly as displayed, keyed by the conversation's stable id.
 * Best-effort — a failure never blocks chat.
 */
async function persistSessionSnapshot(session: AiChatSession | undefined): Promise<void> {
  if (!session || !isTauri()) return;
  try {
    await tauriInvoke("ai_chat_save_snapshot", {
      sessionId: session.id,
      data: JSON.stringify({ version: 1, ...session }),
    });
  } catch (err) {
    console.error("[aiChatStore] snapshot save failed:", err);
  }
}

// ── Store ───────────────────────────────────────────────────────────

export const useAiChatStore = create<AiChatStoreState>((set, get) => ({
  sessions: new Map(),
  activeSessionId: null,
  providerStatus: [],
  reviewMode: "advisory",
  autoReview: false,
  maxReviewIterations: 3,
  _pendingEvents: new Map(),
  _sessionAliases: new Map(),

  _drainPendingEvents(sessionId: string): void {
    const pending = get()._pendingEvents.get(sessionId);
    if (!pending || pending.length === 0) return;
    set((s) => {
      const pe = new Map(s._pendingEvents);
      pe.delete(sessionId);
      return { _pendingEvents: pe };
    });
    const { handleEvent } = get();
    for (const evt of pending) handleEvent(evt);
  },

  // ── Getters ─────────────────────────────────────────────────────

  activeSession(): AiChatSession | null {
    const { sessions, activeSessionId } = get();
    if (!activeSessionId) return null;
    return sessions.get(activeSessionId) ?? null;
  },

  // ── Provider Detection ────────────────────────────────────────────

  async checkProviders(): Promise<void> {
    if (!isTauri()) {
      set({ providerStatus: [] });
      return;
    }
    try {
      const result = await tauriInvoke<ProviderStatus[]>("ai_chat_check_providers");
      set({ providerStatus: result });
    } catch {
      set({ providerStatus: [] });
    }
  },

  // ── Session Actions ─────────────────────────────────────────────

  async createSession(
    provider: AiProvider,
    cwd: string,
    prompt: string,
    resumeSessionId?: string,
  ): Promise<string> {
    assertTauri("AI Chat");

    // Create empty session locally first
    const tempId = crypto.randomUUID();
    const userMessage: DerivedMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: prompt,
      isStreaming: false,
      eventSeqRange: [0, 0],
    };

    const session: AiChatSession = {
      ...createEmptySession(tempId, provider, cwd, prompt.slice(0, 60)),
      messages: [userMessage],
      // The user message occupies seq 0; events are rebased to a monotonic
      // sequence starting at 1 (see handleEvent) so ordering is always correct,
      // even across a --resume where the CLI restarts its own seq at 0.
      lastSeq: 0,
      runStartedAt: new Date().toISOString(),
    };

    set((s) => {
      const sessions = new Map(s.sessions);
      sessions.set(tempId, session);
      return { sessions, activeSessionId: tempId };
    });

    const envVars = await loadSecretEnvForCwd(cwd).catch(() => ({}));

    // Call Rust backend (Tauri auto-converts camelCase → snake_case)
    const info = await tauriInvoke<SessionInfo>("ai_chat_create", {
      provider,
      cwd,
      prompt,
      resumeSessionId: resumeSessionId ?? null,
      guardrails: null,
      envVars,
      env_vars: envVars,
    });
    // Update session with real ID and state from backend. The backend returns
    // state: "running" immediately after spawning the CLI, so adopt it here
    // instead of waiting for the async session_start event — otherwise the
    // "thinking" indicator never shows during the CLI's cold start.
    set((s) => {
      const sessions = new Map(s.sessions);
      const existing = sessions.get(tempId);
      if (existing) {
        sessions.delete(tempId);
        sessions.set(info.session_id, {
          ...existing,
          id: info.session_id,
          state: (info.state as SessionState) ?? existing.state,
        });
      }
      return { sessions, activeSessionId: info.session_id };
    });

    get()._drainPendingEvents(info.session_id);

    // Snapshot immediately so the user's first message survives even if the app
    // closes before the answer arrives.
    void persistSessionSnapshot(get().sessions.get(info.session_id));

    return info.session_id;
  },

  async sendMessage(sessionId: string, message: string): Promise<void> {
    assertTauri("AI Chat");

    const session = get().sessions.get(sessionId);
    if (!session) return;

    // Add user message locally
    const userMessage: DerivedMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: message,
      isStreaming: false,
      eventSeqRange: [session.lastSeq + 1, session.lastSeq + 1],
    };

    set((s) => {
      const sessions = new Map(s.sessions);
      const current = sessions.get(sessionId);
      if (current) {
        sessions.set(sessionId, {
          ...current,
          state: "running" as SessionState,
          messages: [...current.messages, userMessage],
          // Reserve this seq for the user message so the resumed answer's events
          // (rebased monotonically in handleEvent) come after it.
          lastSeq: current.lastSeq + 1,
          runStartedAt: new Date().toISOString(),
        });
      }
      return { sessions };
    });

    // Persist the follow-up question right away (crash-safety before the answer).
    void persistSessionSnapshot(get().sessions.get(sessionId));

    // CLI `-p` mode is one-shot: process exits after responding.
    // For follow-up messages, spawn a new process with `--resume`.
    if (session.state === "completed" || session.state === "idle") {
      const resumeId = session.providerSessionId;
      const envVars = await loadSecretEnvForCwd(session.cwd).catch(() => ({}));
      const info = await tauriInvoke<SessionInfo>("ai_chat_create", {
        provider: session.provider,
        cwd: session.cwd,
        prompt: message,
        resumeSessionId: resumeId ?? null,
        guardrails: null,
        envVars,
        env_vars: envVars,
      });

      // Alias: route events for the new CLI session into this conversation. Their
      // seq is rebased to this session's monotonic sequence in handleEvent, so the
      // follow-up user message reserved above stays ordered before the new answer.
      set((s) => {
        const aliases = new Map(s._sessionAliases);
        aliases.set(info.session_id, sessionId);
        return { _sessionAliases: aliases };
      });

      get()._drainPendingEvents(info.session_id);
      return;
    }

    await tauriInvoke("ai_chat_send", {
      sessionId,
      message,
    });
  },

  async stopSession(sessionId: string): Promise<void> {
    assertTauri("AI Chat");

    await tauriInvoke("ai_chat_stop", { sessionId });

    set((s) => {
      const sessions = new Map(s.sessions);
      const session = sessions.get(sessionId);
      if (session) {
        sessions.set(sessionId, {
          ...session,
          state: "completed",
        });
      }
      return { sessions };
    });
  },

  setActiveSession(sessionId: string | null): void {
    set({ activeSessionId: sessionId });
  },

  // ── Hydration ──────────────────────────────────────────────────

  async hydrateSession(sessionId: string): Promise<boolean> {
    if (!isTauri()) return false;

    // Already loaded — skip
    if (get().sessions.has(sessionId)) return true;

    try {
      // Prefer the full-conversation snapshot: it includes user messages and all
      // resumed turns, which the raw per-run event log cannot restore.
      const snapshotRaw = await tauriInvoke<string | null>("ai_chat_load_snapshot", {
        sessionId,
      });
      if (snapshotRaw) {
        try {
          const snap = JSON.parse(snapshotRaw) as Partial<AiChatSession>;
          const restored: AiChatSession = {
            ...createEmptySession(
              sessionId,
              (snap.provider ?? "claude-code") as AiProvider,
              snap.cwd ?? "",
              snap.title ?? "Restored chat",
            ),
            ...snap,
            id: sessionId,
            // The CLI process is long dead — never restore a mid-turn "running" state.
            state: snap.state === "running" ? "completed" : (snap.state ?? "completed"),
          };
          set((s) => {
            const sessions = new Map(s.sessions);
            sessions.set(sessionId, restored);
            return { sessions };
          });
          return true;
        } catch (err) {
          console.error("[aiChatStore] snapshot parse failed, replaying event log:", err);
        }
      }

      // Fallback: replay the raw event log (older chats — user messages absent).
      const events = await tauriInvoke<AiEvent[]>("ai_chat_load_session", { sessionId });
      if (!events || events.length === 0) return false;

      // Determine provider from first session_start event, fallback to claude-code
      const startEvent = events.find((e) => e.kind === "session_start");
      const provider = (startEvent?.source_provider ?? "claude-code") as AiProvider;

      // Determine cwd from session_start payload
      const cwd = startEvent
        ? ((startEvent.payload as SessionStartPayload).cwd ?? "")
        : "";

      // Derive title from first user prompt or first text
      const firstText = events.find((e) => e.kind === "text_delta");
      const title = firstText
        ? ((firstText.payload as TextDeltaPayload).text ?? "").slice(0, 60)
        : "Restored session";

      // Create empty session and replay all events
      const session = createEmptySession(sessionId, provider, cwd, title);
      set((s) => {
        const sessions = new Map(s.sessions);
        sessions.set(sessionId, session);
        return { sessions };
      });

      // Replay events through handleEvent
      const { handleEvent } = get();
      for (const event of events) {
        handleEvent(event);
      }

      // Determine final state based on last event
      const lastEvent = events[events.length - 1];
      let finalState: SessionState = "idle";
      if (lastEvent.kind === "session_end") {
        finalState = "completed";
      } else if (lastEvent.kind === "error") {
        finalState = "error";
      }

      set((s) => {
        const sessions = new Map(s.sessions);
        const current = sessions.get(sessionId);
        if (current) {
          sessions.set(sessionId, { ...current, state: finalState });
        }
        return { sessions };
      });

      return true;
    } catch {
      return false;
    }
  },

  // ── Event Handler ───────────────────────────────────────────────

  handleEvent(event: AiEvent): void {
    // Resolve alias: resumed sessions emit under a new provider session ID.
    const resolvedId = get()._sessionAliases.get(event.session_id) ?? event.session_id;

    // Session not loaded yet (events can arrive before ai_chat_create resolves):
    // buffer and bail. Drained by _drainPendingEvents once the session exists.
    if (!get().sessions.has(resolvedId)) {
      set((state) => {
        const pending = new Map(state._pendingEvents);
        const buf = pending.get(event.session_id) ?? [];
        pending.set(event.session_id, [...buf, event]);
        return { _pendingEvents: pending };
      });
      return;
    }

    // Canonical event (rewritten session id + monotonic seq) is computed inside the
    // reducer from the session's own lastSeq; captured here for the side effects below.
    let canonicalEvent: AiEvent | null = null;

    // Pure state update — no cross-store writes, no side effects that can throw.
    set((state) => {
      const sessions = new Map(state.sessions);
      const session = sessions.get(resolvedId);
      if (!session) return state; // race: session removed between check and set

      // Rebase every event onto this conversation's own monotonic sequence. A
      // `--resume` spawns a fresh CLI whose seq restarts at 0; ordering by arrival
      // instead keeps the follow-up user message above its answer.
      const seq = session.lastSeq + 1;
      canonicalEvent = { ...event, session_id: resolvedId, seq };

      const updated: AiChatSession = { ...session };
      updated.events = [...session.events, canonicalEvent];
      updated.lastSeq = seq;

      switch (canonicalEvent.kind) {
        case "session_start": {
          const p = canonicalEvent.payload as SessionStartPayload;
          updated.state = "running";
          updated.runStartedAt = updated.runStartedAt ?? canonicalEvent.ts;
          if (p.provider_session_id) {
            updated.providerSessionId = p.provider_session_id;
          }
          break;
        }

        case "text_delta": {
          const p = canonicalEvent.payload as TextDeltaPayload;
          const msgs = [...updated.messages];
          const lastMsg = msgs[msgs.length - 1];
          if (lastMsg && lastMsg.isStreaming && lastMsg.role === "assistant") {
            msgs[msgs.length - 1] = {
              ...lastMsg,
              content: lastMsg.content + p.text,
              eventSeqRange: [lastMsg.eventSeqRange[0], canonicalEvent.seq],
            };
          } else {
            msgs.push({
              id: `msg-${canonicalEvent.seq}`,
              role: "assistant",
              provider: canonicalEvent.source_provider,
              content: p.text,
              isStreaming: true,
              eventSeqRange: [canonicalEvent.seq, canonicalEvent.seq],
            });
          }
          updated.messages = msgs;
          break;
        }

        case "text_complete": {
          const msgs = [...updated.messages];
          const lastMsg = msgs[msgs.length - 1];
          if (lastMsg && lastMsg.isStreaming) {
            msgs[msgs.length - 1] = { ...lastMsg, isStreaming: false };
          }
          updated.messages = msgs;
          break;
        }

        case "tool_start": {
          const p = canonicalEvent.payload as ToolStartPayload;
          updated.toolActivity = [
            ...updated.toolActivity,
            {
              id: p.tool_id,
              toolName: p.tool_name,
              inputSummary: p.tool_input_summary,
              status: "running",
              eventSeq: canonicalEvent.seq,
            },
          ];
          break;
        }

        case "tool_output": {
          const p = canonicalEvent.payload as ToolOutputPayload;
          updated.toolActivity = updated.toolActivity.map((t) =>
            t.id === p.tool_id
              ? { ...t, status: p.status, outputSummary: p.output_summary }
              : t,
          );
          break;
        }

        case "file_change": {
          const p = canonicalEvent.payload as FileChangePayload;
          updated.fileChanges = [...updated.fileChanges, p];
          break;
        }

        case "cost": {
          const p = canonicalEvent.payload as CostPayload;
          updated.totalCostUsd += p.cost_usd;
          updated.totalInputTokens += p.input_tokens;
          updated.totalOutputTokens += p.output_tokens;
          // Attach cost to last message
          const msgs = [...updated.messages];
          const lastMsg = msgs[msgs.length - 1];
          if (lastMsg) {
            msgs[msgs.length - 1] = { ...lastMsg, costUsd: p.cost_usd };
          }
          updated.messages = msgs;
          break;
        }

        case "error": {
          const p = canonicalEvent.payload as ErrorPayload;
          updated.state = p.recoverable ? updated.state : "error";
          updated.messages = [
            ...updated.messages,
            {
              id: `sys-${canonicalEvent.seq}`,
              role: "system",
              content: p.message,
              isStreaming: false,
              eventSeqRange: [canonicalEvent.seq, canonicalEvent.seq],
            },
          ];
          break;
        }

        case "session_end": {
          const p = canonicalEvent.payload as SessionEndPayload;
          updated.state = "completed";
          if (p.provider_session_id) {
            updated.providerSessionId = p.provider_session_id;
          }
          break;
        }

        case "status":
          // Status events are informational; no derived state change needed.
          break;
      }

      sessions.set(resolvedId, updated);
      return { sessions };
    });

    // Session was removed between the buffer check and the reducer — nothing to do.
    if (!canonicalEvent) return;
    const applied: AiEvent = canonicalEvent;

    // Side effects run OUTSIDE the reducer so a throw can never abort event
    // application (the "stuck on Thinking" bug: a throwing activity bridge dropped
    // session_end and left state "running"). Each is independently guarded, and we
    // no longer mutate another store from inside a reducer.
    try {
      const chatTabState = useChatTabStore.getState();
      const matchingTab = chatTabState.findTabBySessionId(resolvedId);
      if (matchingTab) {
        const { tab, projectId } = matchingTab;
        if (applied.kind === "text_delta" || applied.kind === "text_complete") {
          if (chatTabState.activeTabId[projectId] !== tab.id) {
            chatTabState.markUnread(tab.id);
          }
        }
        if (applied.kind === "session_start") {
          chatTabState.updateStatus(tab.id, "running");
        } else if (applied.kind === "session_end") {
          chatTabState.updateStatus(tab.id, "completed");
        } else if (applied.kind === "error") {
          chatTabState.updateStatus(tab.id, "error");
        }
      }
    } catch (err) {
      console.error("[aiChatStore] tab-status sync failed:", err);
    }

    if (applied.kind !== "text_delta" && applied.kind !== "text_complete") {
      try {
        bridgeAiEventToActivity(applied);
      } catch (err) {
        console.error("[aiChatStore] activity bridge failed:", err);
      }
    }

    // Snapshot the whole conversation once a turn finishes so it (user messages
    // and all resumed turns included) restores after an app restart.
    if (applied.kind === "session_end") {
      void persistSessionSnapshot(get().sessions.get(resolvedId));
    }
  },

  // ── Review ──────────────────────────────────────────────────────

  async generateReviewArtifact(sessionId: string): Promise<ReviewArtifact | null> {
    const session = get().sessions.get(sessionId);
    if (!session) return null;

    // Extract assistant summary from last non-streaming assistant message
    const assistantMessages = session.messages.filter(
      (m) => m.role === "assistant" && !m.isStreaming,
    );
    const lastAssistant = assistantMessages[assistantMessages.length - 1];
    const providerSummary = lastAssistant?.content ?? "";

    // Get original user prompt (first user message)
    const firstUser = session.messages.find((m) => m.role === "user");
    const originalPrompt = firstUser?.content ?? "";

    // Get git diff if in Tauri
    let gitDiff = "";
    if (isTauri()) {
      try {
        gitDiff = await tauriInvoke<string>("git_diff", { cwd: session.cwd });
      } catch {
        // Git diff may not be available; that's OK
        gitDiff = "(git diff unavailable)";
      }
    }

    // Calculate duration from events
    let durationMs = 0;
    if (session.events.length >= 2) {
      const first = new Date(session.events[0].ts).getTime();
      const last = new Date(session.events[session.events.length - 1].ts).getTime();
      durationMs = last - first;
    }

    const artifact: ReviewArtifact = {
      cwd: session.cwd,
      original_prompt: originalPrompt,
      provider_summary: providerSummary,
      file_changes: [...session.fileChanges],
      git_diff: gitDiff,
      total_cost_usd: session.totalCostUsd,
      event_count: session.events.length,
      duration_ms: durationMs,
    };

    return artifact;
  },

  async startReview(sessionId: string, reviewerProvider: AiProvider): Promise<void> {
    const artifact = await get().generateReviewArtifact(sessionId);
    if (!artifact) return;

    const session = get().sessions.get(sessionId);
    if (!session) return;

    // Mark the original session as reviewing
    set((s) => {
      const sessions = new Map(s.sessions);
      const current = sessions.get(sessionId);
      if (current) {
        sessions.set(sessionId, { ...current, state: "reviewing" });
      }
      return { sessions };
    });

    // Create a new session with the review prompt
    const reviewPrompt = buildReviewPrompt(artifact);
    await get().createSession(reviewerProvider, session.cwd, reviewPrompt);
  },

  async applyFeedback(sessionId: string, result: ReviewResult): Promise<void> {
    const session = get().sessions.get(sessionId);
    if (!session) return;

    // Add the review result as a system message
    const summaryParts = [
      `Review verdict: ${result.verdict}`,
      result.summary,
    ];

    if (result.findings.length > 0) {
      summaryParts.push(
        "",
        "Findings:",
        ...result.findings.map(
          (f) => `  [${f.severity}] ${f.file}: ${f.description}`,
        ),
      );
    }

    if (result.suggested_actions.length > 0) {
      summaryParts.push("", "Suggested actions:", ...result.suggested_actions.map((a) => `  - ${a}`));
    }

    const systemMessage: DerivedMessage = {
      id: `review-${Date.now()}`,
      role: "system",
      content: summaryParts.join("\n"),
      isStreaming: false,
      eventSeqRange: [session.lastSeq + 1, session.lastSeq + 1],
    };

    set((s) => {
      const sessions = new Map(s.sessions);
      const current = sessions.get(sessionId);
      if (current) {
        sessions.set(sessionId, {
          ...current,
          messages: [...current.messages, systemMessage],
        });
      }
      return { sessions };
    });

    // If approved and review mode is gated, mark as completed
    if (result.verdict === "approved") {
      set((s) => {
        const sessions = new Map(s.sessions);
        const current = sessions.get(sessionId);
        if (current) {
          sessions.set(sessionId, { ...current, state: "completed" });
        }
        return { sessions };
      });
    }
  },

  setReviewMode(mode: ReviewMode): void {
    set({ reviewMode: mode });
  },

  setAutoReview(enabled: boolean): void {
    set({ autoReview: enabled });
  },

  // ── Settings ────────────────────────────────────────────────────

  setMaxReviewIterations(n: number): void {
    set({ maxReviewIterations: n });
  },
}));

// Re-export DEFAULT_GUARDRAILS for convenience
export { DEFAULT_GUARDRAILS };
